import { useState } from 'react';
import {
  Modal, Table, Group, Text, Button, TextInput, Select, Switch, NumberInput,
  ActionIcon, Badge, Stack, Divider, Tooltip, Box,
} from '@mantine/core';
import { IconPlus, IconPencil, IconTrash, IconCheck, IconX } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { EmptyState } from './EmptyState';
import { confirmDelete } from '../lib/confirm';

/** Eski `ExpenseCategory` enumi — yangi tur shu "guruh"ga bog'lanadi (hisobotlar uchun). */
export const LEGACY_CATEGORIES = ['SALARY', 'RENT', 'UTILITY', 'SUPPLY', 'TAX', 'MAINTENANCE', 'OTHER'] as const;
export type LegacyCategory = (typeof LEGACY_CATEGORIES)[number];

/** GET /expenses/categories elementi */
export interface ExpenseType {
  id: string;
  name: string;
  legacyCategory: LegacyCategory;
  isActive: boolean;
  order: number;
  expenseCount: number;
}

/** Tahrirlanayotgan qatorning lokal holati */
interface EditState { id: string; name: string; legacyCategory: LegacyCategory; isActive: boolean; order: number }

/**
 * T11 — XARAJAT TURLARINI BOSHQARISH.
 *
 * Turlar admin tomonidan yaratiladi, lekin har biri eski `ExpenseCategory` enumiga
 * (`legacyCategory` — "guruh") bog'lanadi: xarajat yozilganda ikkalasi birga
 * saqlanadi, shu tufayli mavjud moliyaviy hisobotlar (kirim/chiqim, maosh) o'zgarishsiz
 * ishlaydi. Guruhning o'zgarishi FAQAT keyingi xarajatlarga ta'sir qiladi.
 *
 * O'chirish faqat hech qayerda ishlatilmagan tur uchun — aks holda backend 409
 * qaytaradi va foydalanuvchiga "nofaol qilish" taklif etiladi (tarix buzilmasin).
 */
export function ExpenseCategoriesModal({
  opened, onClose, items,
}: {
  opened: boolean;
  onClose: () => void;
  items: ExpenseType[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [newName, setNewName] = useState('');
  const [newLegacy, setNewLegacy] = useState<LegacyCategory>('OTHER');
  const [edit, setEdit] = useState<EditState | null>(null);

  const legacyLabel = (c: LegacyCategory) => t(`expenseCat.${c}`);
  const legacyData = LEGACY_CATEGORIES.map((c) => ({ value: c, label: legacyLabel(c) }));

  const errMsg = (e: unknown) =>
    (e as { response?: { data?: { error?: string } } })?.response?.data?.error || t('common.error');
  const showErr = (e: unknown) => notifications.show({ color: 'red', message: errMsg(e) });

  /** Turlar o'zgargach: ro'yxat, chiplar va xarajatlar jadvali (tur nomi ko'rinadi) yangilanadi. */
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['expense-categories'] });
    qc.invalidateQueries({ queryKey: ['expenses-summary'] });
    qc.invalidateQueries({ queryKey: ['expenses'] });
    qc.invalidateQueries({ queryKey: ['expense-detail'] });
  };

  const createMut = useMutation({
    mutationFn: () => api.post('/expenses/categories', { name: newName.trim(), legacyCategory: newLegacy }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('expenseTypes.added') });
      setNewName('');
      setNewLegacy('OTHER');
      invalidate();
    },
    onError: showErr,
  });

  const patchMut = useMutation({
    mutationFn: (v: { id: string; body: Partial<Omit<EditState, 'id'>> }) =>
      api.patch(`/expenses/categories/${v.id}`, v.body),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('expenseTypes.updated') });
      setEdit(null);
      invalidate();
    },
    onError: showErr,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/expenses/categories/${id}`),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('expenseTypes.deleted') });
      invalidate();
    },
    // 409 — tur ishlatilmoqda: server matni "nofaol qiling" deb aytadi
    onError: showErr,
  });

  const submitNew = () => {
    if (!newName.trim()) {
      notifications.show({ color: 'red', message: t('expenseTypes.nameRequired') });
      return;
    }
    createMut.mutate();
  };

  const saveEdit = () => {
    if (!edit) return;
    if (!edit.name.trim()) {
      notifications.show({ color: 'red', message: t('expenseTypes.nameRequired') });
      return;
    }
    patchMut.mutate({
      id: edit.id,
      body: {
        name: edit.name.trim(),
        legacyCategory: edit.legacyCategory,
        isActive: edit.isActive,
        order: edit.order,
      },
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} size="lg" title={t('expenseTypes.title')}>
      {/* Yangi tur */}
      <Group align="flex-end" gap="sm" wrap="wrap">
        <TextInput
          label={t('expenseTypes.name')}
          placeholder={t('expenseTypes.namePlaceholder')}
          value={newName}
          onChange={(e) => setNewName(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submitNew(); }}
          style={{ flex: '1 1 200px' }}
        />
        <Select
          label={t('expenseTypes.group')}
          description={t('expenseTypes.groupHint')}
          data={legacyData}
          value={newLegacy}
          onChange={(v) => setNewLegacy((v as LegacyCategory) || 'OTHER')}
          allowDeselect={false}
          w={190}
        />
        <Button leftSection={<IconPlus size={16} />} onClick={submitNew} loading={createMut.isPending}>
          {t('expenseTypes.add')}
        </Button>
      </Group>

      <Divider my="md" />

      {!items.length ? (
        <EmptyState title={t('expenseTypes.empty')} description={t('expenseTypes.emptyDesc')} height={160} />
      ) : (
        <Table.ScrollContainer minWidth={620}>
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('expenseTypes.name')}</Table.Th>
                <Table.Th>{t('expenseTypes.group')}</Table.Th>
                <Table.Th w={90}>{t('expenseTypes.order')}</Table.Th>
                <Table.Th w={90}>{t('expenseTypes.usage')}</Table.Th>
                <Table.Th w={110}>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((c) => {
                const editing = edit?.id === c.id;
                return (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      {editing ? (
                        <TextInput
                          value={edit.name}
                          onChange={(e) => setEdit({ ...edit, name: e.currentTarget.value })}
                          w={180}
                        />
                      ) : (
                        <Stack gap={2}>
                          <Text fz="sm" fw={500}>{c.name}</Text>
                          {!c.isActive && <Badge size="xs" color="gray" variant="light">{t('expenseTypes.inactive')}</Badge>}
                        </Stack>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {editing ? (
                        <Select
                          data={legacyData}
                          value={edit.legacyCategory}
                          onChange={(v) => setEdit({ ...edit, legacyCategory: (v as LegacyCategory) || 'OTHER' })}
                          allowDeselect={false}
                          w={160}
                        />
                      ) : (
                        <Text fz="sm" c="dimmed">{legacyLabel(c.legacyCategory)}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {editing ? (
                        <NumberInput
                          value={edit.order}
                          onChange={(v) => setEdit({ ...edit, order: Number(v) || 0 })}
                          min={0}
                          w={80}
                        />
                      ) : (
                        <Text fz="sm">{c.order}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={c.expenseCount ? 'brand' : 'gray'}>{c.expenseCount}</Badge>
                    </Table.Td>
                    <Table.Td>
                      {editing ? (
                        <Group gap={4} wrap="nowrap">
                          <Switch
                            size="xs"
                            checked={edit.isActive}
                            onChange={(e) => setEdit({ ...edit, isActive: e.currentTarget.checked })}
                            aria-label={t('expenseTypes.active')}
                          />
                          <ActionIcon color="green" variant="light" onClick={saveEdit} loading={patchMut.isPending}>
                            <IconCheck size={16} />
                          </ActionIcon>
                          <ActionIcon variant="subtle" onClick={() => setEdit(null)}>
                            <IconX size={16} />
                          </ActionIcon>
                        </Group>
                      ) : (
                        <Group gap={4} wrap="nowrap">
                          <Tooltip label={c.isActive ? t('expenseTypes.deactivate') : t('expenseTypes.activate')} withArrow>
                            <Switch
                              size="xs"
                              checked={c.isActive}
                              onChange={(e) => patchMut.mutate({ id: c.id, body: { isActive: e.currentTarget.checked } })}
                              aria-label={t('expenseTypes.active')}
                            disabled={patchMut.isPending} />
                          </Tooltip>
                          <ActionIcon
                            variant="subtle"
                            onClick={() => setEdit({
                              id: c.id, name: c.name, legacyCategory: c.legacyCategory,
                              isActive: c.isActive, order: c.order,
                            })}
                            aria-label={t('expenseTypes.edit')}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                          {/* Ishlatilayotgan turni o'chirib bo'lmaydi — tugma oldindan bloklanadi
                              (backend ham 409 bilan himoyalaydi) */}
                          <Tooltip
                            label={c.expenseCount ? t('expenseTypes.deleteBlocked') : t('common.delete')}
                            withArrow
                          >
                            <Box>
                              <ActionIcon
                                color="red"
                                variant="subtle"
                                disabled={c.expenseCount > 0}
                                onClick={() => confirmDelete(c.name, () => deleteMut.mutate(c.id))}
                                aria-label={t('common.delete')}
                               loading={deleteMut.isPending}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Box>
                          </Tooltip>
                        </Group>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onClose}>{t('expenseTypes.close')}</Button>
      </Group>
    </Modal>
  );
}
